import { getCurrentBcp47Locale } from "@circle-react/helpers/localeHelpers";
import type { TimeValue } from "@circleco/compass/components/TimeInput";

export const distanceBetweenDates = (date1: Date, date2: Date) =>
  Math.round((date2.getTime() - date1.getTime()) / (1000 * 60 * 60 * 24));

export const getTimeValue = (date: Date): TimeValue => ({
  hours: date.getHours(),
  minutes: date.getMinutes(),
});

export const combineDateAndTime = (date: Date, time: TimeValue): Date => {
  const combined = new Date(date);
  combined.setHours(time.hours, time.minutes, 0, 0);
  return combined;
};

export const dateIsPast = (dateString: string) => {
  const now = new Date();
  const date = new Date(dateString);
  return !date.valueOf() || date < now;
};

export const getElapsedSeconds = (dateString: string) => {
  const now = new Date();
  const date = new Date(dateString);
  return Math.floor((now.getTime() - date.getTime()) / 1000);
};

export const secondsToTimeString = (
  seconds: number,
  { dropHours = true }: { dropHours?: boolean } = {},
) => {
  const addPadding = (number: number) => ("0" + number).slice(-2);

  const isLessThanOneHour = seconds >= 0 && seconds < 3600;

  if (dropHours && isLessThanOneHour) {
    const mins = Math.floor(seconds / 60);
    const rest = seconds - mins * 60;
    return `${addPadding(mins)}:${addPadding(rest)}`;
  }

  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds - hours * 3600) / 60);
  const rest = seconds - mins * 60 - hours * 3600;
  return `${addPadding(hours)}:${addPadding(mins)}:${addPadding(rest)}`;
};

export const wait = (ms: number) =>
  new Promise(resolve => setTimeout(resolve, ms));

export const minimumDelay = async <Type>(
  promise: Promise<Type>,
  ms = 1500,
): Promise<Type> => {
  const waitPromise = wait(ms);
  const [result] = await Promise.all([promise, waitPromise]);
  return result;
};

export const addDaysToDate = (startDate: string, days: number) => {
  const date = new Date(startDate);
  date.setDate(date.getDate() + days);
  return date;
};

export const roundToNextHour = (date: Date) => {
  date.setMinutes(date.getMinutes() + 60);
  date.setMinutes(0, 0, 0);

  return date;
};

export const addSecondsToDate = (date: string, seconds: number) => {
  const newDate = date ? new Date(date) : new Date();
  newDate.setSeconds(newDate.getSeconds() + seconds);
  return newDate;
};

export const wasMinutesAgo = (date: string, minutes = 25) =>
  new Date().getTime() >= new Date(date).getTime() - minutes * 60 * 1000;

export const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export const getTimezoneShort = (timeZone?: string, date = new Date()) => {
  const locale = getCurrentBcp47Locale();
  const formatter = new Intl.DateTimeFormat([locale, "en-US"], {
    timeZone: timeZone,
    timeZoneName: "short",
  });

  const parts = formatter.formatToParts(date);

  return parts.find(part => part.type == "timeZoneName")?.value || "";
};

export const getDateFromString = (dateStr: string) => {
  if (!dateStr) return new Date();

  return new Date(dateStr);
};

export const weekdayOccurrence = (startDate: string) => {
  const date = new Date(startDate);
  return Math.ceil(date.getDate() / 7);
};

export const getNextQuarter = (time: Date) => {
  const quarter = 15 * 60 * 1000; // 15 minutes in milliseconds
  return new Date(Math.ceil(time.getTime() / quarter) * quarter);
};

export const isMidnight = (date: Date) =>
  date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0;

export const DEFAULT_TIME: TimeValue = { hours: 9, minutes: 0 };

const formatTimeLabel = (hours: number, minutes: number): string => {
  const period = hours < 12 ? "AM" : "PM";
  const displayHour = hours % 12 || 12;
  const displayMinutes = String(minutes).padStart(2, "0");
  return `${displayHour}:${displayMinutes}${period}`;
};

export const timeToSelectValue = (time: TimeValue): string =>
  `${String(time.hours).padStart(2, "0")}:${String(time.minutes).padStart(2, "0")}`;

export const selectValueToTime = (value: string): TimeValue => {
  const [h, m] = value.split(":").map(Number);
  return { hours: h, minutes: m };
};

export const generateTimeOptions = (
  stepMinutes = 15,
): { label: string; value: string }[] => {
  const options: { label: string; value: string }[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += stepMinutes) {
      options.push({
        label: formatTimeLabel(h, m),
        value: timeToSelectValue({ hours: h, minutes: m }),
      });
    }
  }
  return options;
};

export const toDate = (value: unknown): Date | undefined => {
  if (value instanceof Date) return value;
  if (typeof value !== "string" && typeof value !== "number") return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

export const ONE_DAY_FROM_NOW = new Date(Date.now() + 24 * 60 * 60 * 1000);

export const getLocalDateTimeDisplayFormat = (): string => {
  const timezone = getTimezoneShort(browserTimeZone);
  const locale = getCurrentBcp47Locale();
  const is24h = !/AM|PM/i.exec(
    new Intl.DateTimeFormat(locale, { hour: "numeric" }).format(new Date()),
  );
  const timeToken = is24h ? "HH:mm" : "h:mm a";
  return `MMM d, yyyy, ${timeToken} ('${timezone}')`;
};
